import { useEffect, useRef, useState } from "react";

// After drawing a room with no room chosen first: pick one of the rooms already in the schedule that are not placed
// yet (the estimator wrote the room list down on the previous page — they should not have to retype the names), or
// name a new one. Replaces the old "type a name" prompt (feedback #20). Nothing is created until a choice is made.

const fmt = (n) => (Math.round((+n || 0) * 10) / 10).toLocaleString();

export default function RoomPickDialog({ geom, candidates = [], onPick, onNew, onCancel }) {
  const [name, setName] = useState("");
  const input = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);
  useEffect(() => { if (!candidates.length) input.current?.focus(); }, [candidates.length]);

  const submitNew = () => { if (name.trim()) onNew(name.trim()); };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 960, background: "rgba(15,23,42,0.45)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "12vh" }} onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div role="dialog" aria-label="Which room is this?" style={{ width: "min(420px, 92vw)", maxHeight: "70vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.4)", padding: "14px 16px", fontSize: 13 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>Which room is this?</div>
        <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 10 }}>You drew {fmt(geom.lengthFt)}′ × {fmt(geom.widthFt)}′.</div>
        {candidates.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", marginBottom: 4 }}>Not placed yet</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
              {candidates.map(r => (
                <button key={r.RoomID} type="button" onClick={() => onPick(r)}
                  style={{ textAlign: "left", padding: "6px 10px", fontSize: 12.5, fontWeight: 600, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text)" }}>
                  {r.RoomNumber ? `${r.RoomNumber} · ` : ""}{r.RoomName || `Room ${r.RoomID}`}
                </button>
              ))}
            </div>
          </>
        )}
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", marginBottom: 4 }}>{candidates.length ? "Or a new room" : "Name the new room"}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input ref={input} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") submitNew(); }} placeholder="Room name" aria-label="New room name"
            style={{ flex: 1, fontSize: 12.5, padding: "5px 8px", border: "1px solid var(--cs-border)", borderRadius: 6 }} />
          <button type="button" onClick={submitNew} disabled={!name.trim()}
            style={{ fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 6, border: "none", background: "#2563eb", color: "#fff", cursor: name.trim() ? "pointer" : "not-allowed", opacity: name.trim() ? 1 : 0.5 }}>Create</button>
        </div>
        <div style={{ textAlign: "right", marginTop: 10 }}>
          <button type="button" onClick={onCancel} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: "1px solid var(--cs-border)", background: "var(--surface)", cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
